/**
 * PWA Utilities
 * Handles service worker registration, install prompts, and offline functionality
 */

declare global {
  interface Window {
    gtag?: any;
  }
}

let deferredPrompt: BeforeInstallPromptEvent | null = null;
let installPromptShown = false;

/**
 * Initialize PWA features
 */
export function initPWA(): void {
  // Service worker registration is handled by vite-plugin-pwa
  // Register beforeinstallprompt listener
  registerInstallPrompt();

  // Log PWA status
  logPWAStatus();
}

/**
 * Register beforeinstallprompt event listener
 * This event is triggered when the browser determines the app is installable
 */
function registerInstallPrompt(): void {
  window.addEventListener('beforeinstallprompt', (event: Event) => {
    // Prevent the default behavior (browser will show its own prompt)
    event.preventDefault();

    // Store the event for later use
    deferredPrompt = event as BeforeInstallPromptEvent;

    // Show our custom install button/prompt
    showInstallPrompt();
  });

  // Listen for app installed event
  window.addEventListener('appinstalled', () => {
    console.log('PWA was installed');
    deferredPrompt = null;
    hideInstallPrompt();

    // Track installation
    if (typeof window !== 'undefined' && (window as any).gtag) {
      (window as any).gtag('event', 'app_installed');
    }
  });
}

/**
 * Show the install prompt to the user
 */
export function showInstallPrompt(): void {
  if (installPromptShown || !deferredPrompt) return;

  // Create and show a custom install button in the app header
  const installButton = document.getElementById('pwa-install-btn');
  if (installButton) {
    installButton.style.display = 'block';
    installPromptShown = true;
  }
}

/**
 * Hide the install prompt
 */
function hideInstallPrompt(): void {
  const installButton = document.getElementById('pwa-install-btn');
  if (installButton) {
    installButton.style.display = 'none';
    installPromptShown = false;
  }
}

/**
 * Handle the install button click
 * This function should be called from the UI component
 */
export async function handleInstallClick(): Promise<void> {
  if (!deferredPrompt) {
    console.warn('Install prompt is not available');
    return;
  }

  try {
    // Show the install prompt
    deferredPrompt.prompt();

    // Wait for the user response
    const choiceResult = await deferredPrompt.userChoice;

    if (choiceResult.outcome === 'accepted') {
      console.log('PWA installation accepted');
    } else {
      console.log('PWA installation declined');
    }

    // Clear the deferred prompt
    deferredPrompt = null;
    hideInstallPrompt();
  } catch (error) {
    console.error('Error handling install prompt:', error);
  }
}

/**
 * Log PWA and offline status
 */
function logPWAStatus(): void {
  if ('serviceWorker' in navigator) {
    navigator.serviceWorker
      .getRegistrations()
      .then((registrations) => {
        console.log(`Service workers active: ${registrations.length}`);
        registrations.forEach((registration) => {
          console.log('Service Worker scope:', registration.scope);
        });
      })
      .catch((error) => console.error('Error checking service workers:', error));
  }

  // Log online status
  console.log('Online status:', navigator.onLine);
  window.addEventListener('online', () => {
    console.log('App went online');
    showToast('Connection restored', 'success');
  });

  window.addEventListener('offline', () => {
    console.log('App went offline');
    showToast('You are offline - viewing cached content', 'warning');
  });
}

/**
 * Check if the app is running as a PWA
 */
export function isRunningAsPWA(): boolean {
  // Check if app is in standalone mode (on mobile)
  if (window.matchMedia('(display-mode: standalone)').matches) {
    return true;
  }

  // Check if launched from home screen (iOS)
  if (
    navigator.standalone === true ||
    document.referrer.includes('android-app://')
  ) {
    return true;
  }

  // Check if running in full-screen mode (Chrome on Android)
  if (window.innerHeight === screen.height && window.innerWidth === screen.width) {
    return true;
  }

  return false;
}

/**
 * Request persistent storage quota
 * Allows the app to persist data beyond browser's automatic cleanup
 */
export async function requestPersistentStorage(): Promise<boolean> {
  if (!('storage' in navigator) || !('persist' in navigator.storage)) {
    console.warn('Persistent storage is not supported');
    return false;
  }

  try {
    const isPersistent = await navigator.storage.persist();
    console.log('Persistent storage:', isPersistent ? 'granted' : 'denied');
    return isPersistent;
  } catch (error) {
    console.error('Error requesting persistent storage:', error);
    return false;
  }
}

/**
 * Get available storage space
 */
export async function getStorageInfo(): Promise<{
  usage: number;
  quota: number;
  percentage: number;
} | null> {
  if (!('storage' in navigator) || !('estimate' in navigator.storage)) {
    console.warn('Storage API is not supported');
    return null;
  }

  try {
    const estimate = await navigator.storage.estimate();
    return {
      usage: estimate.usage ?? 0,
      quota: estimate.quota ?? 0,
      percentage: ((estimate.usage ?? 0) / (estimate.quota ?? 1)) * 100,
    };
  } catch (error) {
    console.error('Error getting storage info:', error);
    return null;
  }
}

/**
 * Simple toast notification (requires react-hot-toast)
 */
function showToast(message: string, type: 'success' | 'error' | 'warning' | 'info' = 'info'): void {
  // This will be called after react-hot-toast is initialized
  // For now, just log to console
  console.log(`[${type.toUpperCase()}] ${message}`);
}

/**
 * Register for push notifications
 * Requires push notification API and service worker
 */
export async function registerForPushNotifications(): Promise<boolean> {
  if (!('serviceWorker' in navigator) || !('PushManager' in window)) {
    console.warn('Push notifications are not supported');
    return false;
  }

  try {
    const registration = await navigator.serviceWorker.ready;
    const subscription = await registration.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: process.env.REACT_APP_VAPID_PUBLIC_KEY,
    });

    console.log('Push subscription:', subscription);
    return true;
  } catch (error) {
    console.error('Error registering for push notifications:', error);
    return false;
  }
}

/**
 * Disable type checking for BeforeInstallPromptEvent
 * This is a browser API that TypeScript doesn't fully support
 */
declare global {
  interface WindowEventMap {
    beforeinstallprompt: BeforeInstallPromptEvent;
  }

  interface BeforeInstallPromptEvent extends Event {
    prompt: () => Promise<void>;
    userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
  }

  interface Navigator {
    standalone?: boolean;
  }
}

export default {
  initPWA,
  handleInstallClick,
  isRunningAsPWA,
  requestPersistentStorage,
  getStorageInfo,
  registerForPushNotifications,
};
